import { computed, onMounted, onUnmounted, ref } from "@vue/composition-api";
import { MessagePrices } from "@scrile/api-provider/dist/api/MessagePricesProvider";
import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { Subscription } from "@scrile/api-provider/dist/api/SubscriptionProvider";
import { ErrorDTO } from "@scrile/api-provider/dist/lib/apiErrorHandler";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import usePaymentPackage from "@/hooks/usePaymentPackage";
import providers from "@/lib/api";
import createDeferred from "@/lib/deferredPromise";
import emitter from "@/lib/emitter";
import config from "@/config";
import { ADMIN_ID, ERRORS, EVENTS } from "@/const";

const usePaidMessages = (userId?: string) => {
  const { authUser, authorities } = useAuthUser();
  const { findAll: findAllPaymentPackages } = usePaymentPackage();

  const paymentPackages = ref<PaymentPackage[]>([]);
  const messagePrices = ref<MessagePrices>();
  const subscription = ref<Subscription>();
  const subscriptionLevels = ref<SubscriptionLevel[]>([]);
  const freeMessageSentCount = ref(0);
  const processingPayment = ref(false);
  const error = ref(false);

  const isHasFreeIncomingMessagesByLimit = computed(
    () => !subscription.value && freeMessageSentCount.value < (messagePrices.value?.freeMessageLimit ?? 0)
  );
  const priceAmount = computed(() => {
    if (isHasFreeIncomingMessagesByLimit.value) return 0;
    const priceId = subscription.value
      ? messagePrices.value?.subscriberPerSentPriceId
      : messagePrices.value?.perSentPriceId;
    return paymentPackages.value.find(i => i.id === priceId)?.amount ?? 0;
  });
  const priceMediaAmount = computed(() => {
    const priceId = subscription.value
      ? messagePrices.value?.subscriberPerSentWithAttachmentPriceId
      : messagePrices.value?.perSentWithAttachmentPriceId;
    return paymentPackages.value.find(i => i.id === priceId)?.amount ?? 0;
  });
  const isHasAuthoritiesToSendMessages = computed(() =>
    checkAuthorities(authorities.value, ["sendFreeMessageToPaidThreads"])
  );
  const isHasFreeIncomingMessagesByLevel = computed(() => !!subscription.value?.level?.freeIncomingMessages);
  const noNeedGetPrice = computed(
    () =>
      !userId ||
      !config.featurePaidSentMessage ||
      !authUser.value ||
      isHasAuthoritiesToSendMessages.value ||
      isHasFreeIncomingMessagesByLevel.value
  );

  const getPaymentPackages = async () => {
    paymentPackages.value = await findAllPaymentPackages([PaymentPackageEnum.PER_SENT_MESSAGE]);
  };

  const getSubscription = async () => {
    if (!userId || userId === ADMIN_ID || !authUser.value) return;
    const response = await providers.SubscriptionProvider.search(
      { data: { consumerIds: [authUser.value.id], providerId: userId, expired: false } },
      ["id", "levelId", { level: ["id", "freeIncomingMessages"] }]
    );
    subscription.value = response.result[0];
  };

  const getSubscriptionLevels = async () => {
    if (!userId) return;
    subscriptionLevels.value = await providers.SubscriptionLevelProvider.find({ userIds: [userId] }, ["contentCount"]);
  };

  const getPrice = async () => {
    if (!userId) return;
    const response = await providers.MessagePricesProvider.find({ userIds: [userId] });
    messagePrices.value = response.find(i => i.userId === userId);
  };

  const checkPromotion = () => {
    if (
      messagePrices.value?.perSentPriceId !== "0" &&
      messagePrices.value?.subscriberPerSentPriceId === "0" &&
      !subscription.value &&
      freeMessageSentCount.value >= (messagePrices.value?.freeMessageLimit ?? 0) &&
      userId
    ) {
      if (subscriptionLevels.value.some(l => l.contentCount ?? 0 > 0)) {
        emitter.emit(EVENTS.SUBSCRIPTION_OFFER_SHOW, { showTitle: !!messagePrices.value?.freeMessageLimit });
      }
    }
  };

  const getFreeMessagesSentCount = async () => {
    if (!userId) return;
    freeMessageSentCount.value = await providers.MessagePricesProvider.freeMessagesSent(userId);
  };

  const getPriceData = async () => {
    await Promise.all([getPrice(), getFreeMessagesSentCount()]);
    checkPromotion();
  };

  const onSubscribe = async () => {
    await getSubscription();
    messagePrices.value = undefined;
    if (noNeedGetPrice.value) return;
    await getPrice();
  };

  const closeError = () => {
    error.value = false;
  };

  onMounted(async () => {
    await Promise.all([getSubscription()]);
    if (noNeedGetPrice.value) return;
    await Promise.all([getPriceData(), getPaymentPackages(), getSubscriptionLevels()]);
    emitter.on<any>(EVENTS.SUBSCRIPTION_STATUS_CHANGED, onSubscribe);
  });

  onUnmounted(() => {
    emitter.off<any>(EVENTS.SUBSCRIPTION_STATUS_CHANGED, onSubscribe);
  });

  const deferred = createDeferred();
  const showPaymentConfirm = ref(false);
  const showPaymentMediaConfirm = ref(false);

  const onPaymentConfirm = (response: boolean) => {
    showPaymentConfirm.value = false;
    deferred.resolve(response);
    const newDeferred = createDeferred();
    deferred.promise = newDeferred.promise;
    deferred.resolve = newDeferred.resolve;
  };

  const createSendPaidMessage = (
    sendMessage: (text: string) => Promise<Message[] | undefined>,
    hasAttachments: boolean
  ) => {
    if (
      !config.featurePaidSentMessage ||
      isHasAuthoritiesToSendMessages.value ||
      isHasFreeIncomingMessagesByLevel.value ||
      !userId
    ) {
      return sendMessage;
    }
    return async (text: string) => {
      if (!userId) {
        throw new Error("UserId required");
      }
      try {
        processingPayment.value = true;
        providers.MessagesProvider.setReceiveErrors(false);

        await getPriceData();
        if (priceAmount.value > 0 || (hasAttachments && priceMediaAmount.value > 0)) {
          if (hasAttachments && priceMediaAmount.value > priceAmount.value) {
            showPaymentMediaConfirm.value = true;
          } else {
            showPaymentConfirm.value = true;
          }
          if (!(await deferred.promise)) {
            return;
          }
        }
        return await sendMessage(text);
      } catch (e) {
        if ((e as ErrorDTO[]).some(i => i?.details?.messageTemplate === ERRORS.NOT_ENOUGH_FUNDS_ERROR)) {
          error.value = true;
          return null;
        }
        throw e;
      } finally {
        processingPayment.value = false;
        showPaymentConfirm.value = false;
        showPaymentMediaConfirm.value = false;
        await getFreeMessagesSentCount();
        checkPromotion();
      }
    };
  };

  return {
    priceAmount,
    priceMediaAmount,
    processingPayment,
    showPaymentConfirm,
    showPaymentMediaConfirm,
    error,
    onPaymentConfirm,
    createSendPaidMessage,
    closeError,
  };
};

export default usePaidMessages;
